import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'
import JSZip from 'jszip'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { delUrlFetcher, getBlob, postFormWithProgress } from 'api/swr'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { MyImage } from 'components/MyImage'
import { Template } from 'types/template'

import {
  buildKitFromFace,
  compositeSticker,
  findDots,
  fitFace,
  rasterSize,
  readPixels,
  STICKER_SIZE,
  type Dot,
  type DotKey,
  type Face,
  type Raster,
} from './geometry'
import {
  buildAllZip,
  buildArtistZip,
  buildLangZip,
  buildStarterZip,
  saveBlob,
} from './pack'
import { faceDrawKey, renderEditorFace } from './renderFace'
import { readCachedSrc, writeCachedSrc } from './srcCache'
import StarterKitPreview from './StarterKitPreview'

const LANG_LABEL: Record<string, string> = {
  en: '英语',
  pt: '巴西葡萄牙语',
  es: '拉丁西班牙语',
}
const LANG_ALIAS: Record<string, string> = {
  en: 'en',
  us: 'en',
  pt: 'pt',
  br: 'pt',
  es: 'es',
  mx: 'es',
}
const ID_RE = /^[A-Za-z0-9]{8}$/
const natural = new Intl.Collator(undefined, { numeric: true }).compare

type StickerItem = {
  id: string
  sticker: HTMLImageElement
  locator: HTMLImageElement
  stickerFile: File
  locatorFile: File
  previewUrl: string
  face: Face | null
  error: string | null
  warn: string | null
}

type LangPack = {
  lang: string
  items: StickerItem[]
  notes: string[]
}

type KitState = {
  dots: Record<DotKey, Dot>
  image: Raster
  /** 带色点的 PNG 原字节，起始包里发给画师的就是这一份。 */
  png: Blob
  thumbUrl: string
  label: string
}

type Session = {
  /** 画师包里的标准脸，只用来解脸位，不贴到预览上。 */
  kit: KitState | null
  /** 当前画布五官，不含色点。预览贴的是这张。 */
  editorFace: HTMLCanvasElement | null
  faceThumbUrl: string
  faceError: string
  langs: LangPack[]
  note: string
}

const emptySession = (): Session => ({
  kit: null,
  editorFace: null,
  faceThumbUrl: '',
  faceError: '',
  langs: [],
  note: '',
})

let cacheTid = ''
let cacheSession: Session = emptySession()

const langName = (lang: string) => LANG_LABEL[lang] ?? lang

type SavedStickerIndex = {
  hasSrc?: boolean
  srcSha256?: string
  langs?: Record<string, { count?: number; ids?: string[] }>
}

type SaveStatus = {
  phase: 'pack' | 'upload' | 'write'
  label: string
  percent: number
}

const formatIds = (ids: string[]) =>
  ids.length <= 6 ? ids.join('、') : `${ids.slice(0, 6).join('、')} 等 ${ids.length} 张`

function describeSave(
  groups: { lang: string; ids: string[] }[],
  saved: SavedStickerIndex | undefined,
  savedError: boolean,
  savedLoading: boolean,
) {
  if (savedLoading) return ['正在读取上次保存…']
  if (savedError) return ['没读到上次保存。确认后会按当前列表写入。']
  const previous = saved?.langs ?? {}
  if (!Object.keys(previous).length) {
    return groups.map((group) => `${langName(group.lang)}：首次保存 ${group.ids.length} 张`)
  }
  const lines: string[] = []
  for (const group of groups) {
    const prev = previous[group.lang]
    const name = langName(group.lang)
    if (!prev) {
      lines.push(`${name}：新增加这个语言，${group.ids.length} 张`)
      continue
    }
    if (!prev.ids?.length) {
      const count =
        prev.count === group.ids.length
          ? `仍是 ${group.ids.length} 张`
          : `上次 ${prev.count ?? 0} 张，这次 ${group.ids.length} 张`
      lines.push(`${name}：${count}。上次没有顺序记录，这次按当前列表保存`)
      continue
    }
    const added = group.ids.filter((id) => !prev.ids?.includes(id))
    const removed = prev.ids.filter((id) => !group.ids.includes(id))
    const prevCommon = prev.ids.filter((id) => group.ids.includes(id))
    const nextCommon = group.ids.filter((id) => prev.ids?.includes(id))
    const parts: string[] = []
    if (added.length) parts.push(`新增 ${formatIds(added)}`)
    if (removed.length) parts.push(`去掉 ${formatIds(removed)}`)
    if (prevCommon.join('\0') !== nextCommon.join('\0')) parts.push('顺序有变化')
    if (!parts.length) parts.push('贴纸和顺序没变，仍会按当前脸位重新打包')
    lines.push(`${name}：${parts.join('；')}`)
  }
  for (const lang of Object.keys(previous)) {
    if (!groups.some((group) => group.lang === lang)) {
      lines.push(`${langName(lang)}：这次不更新，线上仍是上次的包`)
    }
  }
  return lines
}

function SortableStickerCard({
  item,
  index,
  disabled,
}: {
  item: StickerItem
  index: number
  disabled: boolean
}) {
  const dragged = useRef(false)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled,
  })
  if (isDragging) dragged.current = true

  return (
    <figure
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 1 : undefined,
      }}
      className="w-[200px] cursor-grab space-y-1 active:cursor-grabbing"
      {...(disabled ? {} : { ...attributes, ...listeners })}
    >
      <div
        onClickCapture={(event) => {
          if (!dragged.current) return
          event.stopPropagation()
          dragged.current = false
        }}
      >
        <MyImage
          alt={item.id}
          src={item.previewUrl || item.sticker.src}
          lazy={false}
          className="h-[200px] w-[200px] rounded border bg-[repeating-conic-gradient(#eee_0_25%,#fff_0_50%)] bg-[length:12px_12px] object-contain"
        />
      </div>
      <figcaption className="space-y-0.5 text-[11px] leading-4">
        <div className="font-mono">
          {index + 1}. {item.id}
        </div>
        {item.face ? (
          <div className="text-muted-foreground">
            tx {item.face.tx} · ty {item.face.ty}
            <br />
            scale {item.face.scale} · rot {item.face.rotation}
          </div>
        ) : null}
        {item.error ? <div className="text-destructive">{item.error}</div> : null}
        {item.warn ? <div className="text-amber-600">{item.warn}</div> : null}
      </figcaption>
    </figure>
  )
}

const canvasToPng = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('无法导出标准脸'))),
      'image/png',
    )
  })

const fileToImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`${file.name} 无法打开`))
    image.src = URL.createObjectURL(file)
  })

const isJunk = (path: string) =>
  path.includes('__MACOSX/') || /(^|\/)(\._[^/]*|\.DS_Store)$/.test(path)

const langOfFolder = (name: string) => {
  const key = name.toLowerCase()
  if (LANG_ALIAS[key]) return LANG_ALIAS[key]
  if (/^[a-z]{2}$/.test(key)) return key
  return null
}

const newStickerId = (taken: Set<string>) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  for (;;) {
    const bytes = crypto.getRandomValues(new Uint8Array(8))
    const id = [...bytes].map((b) => chars[b % 62]).join('')
    if (!taken.has(id)) return id
  }
}

function previewOf(item: StickerItem, face: Raster | null, fitted: Face | null) {
  if (!fitted || !face) return ''
  try {
    return compositeSticker(face, item.sticker, fitted).toDataURL('image/png')
  } catch {
    return ''
  }
}

function repaintLang(pack: LangPack, face: Raster): LangPack {
  return {
    ...pack,
    items: pack.items.map((item) =>
      item.face ? { ...item, previewUrl: previewOf(item, face, item.face) } : item,
    ),
  }
}

function refitLang(pack: LangPack, kit: KitState | null, face: Raster | null): LangPack {
  if (!kit) {
    return {
      ...pack,
      items: pack.items.map((item) => ({
        ...item,
        face: null,
        previewUrl: '',
        error: '目录里没有带色点的标准脸，无法计算位置',
        warn: null,
      })),
    }
  }
  return {
    ...pack,
    items: pack.items.map((item) => {
      const size = rasterSize(item.sticker)
      if (size.w !== STICKER_SIZE || size.h !== STICKER_SIZE) {
        return {
          ...item,
          face: null,
          previewUrl: '',
          error: `贴纸是 ${size.w}×${size.h}，需要 512×512`,
          warn: null,
        }
      }
      const fitted = fitFace(kit.dots, item.locator)
      return {
        ...item,
        face: fitted.face,
        error: fitted.error,
        warn: fitted.warn,
        previewUrl: previewOf(item, face ?? undefined, fitted.face),
      }
    }),
  }
}

const StickerPanel: React.FC = () => {
  const { tid } = useParams<{ tid: string }>()
  const { toast } = useToast()
  const { data: template } = useSWR<Template>(tid ? [`/templates/${tid}`] : null)
  const isNonSquareTemplate =
    template?.fullscreenType === 2 || template?.fullscreenType === 3
  const {
    data: savedPack,
    error: savedError,
    isLoading: savedLoading,
    mutate: refreshSaved,
  } = useSWR<SavedStickerIndex>(tid ? [`/templates/${tid}/stickers`] : null)
  const { data: layers } = useSWR(tid ? ['/layers', { tid }] : null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [session, setSession] = useState<Session>(() =>
    cacheTid === tid ? cacheSession : emptySession(),
  )
  const [busy, setBusy] = useState<'import' | 'zip' | 'starter' | 'save' | 'load' | 'delete' | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [starterOpen, setStarterOpen] = useState(false)
  const [starterAnchors, setStarterAnchors] = useState<Partial<Record<DotKey, Dot>>>({})
  const [faceResult, setFaceResult] = useState<{
    tid: string
    key: string
    generation: number
    face: HTMLCanvasElement
    anchors: Partial<Record<DotKey, Dot>>
  } | null>(null)
  const [deleteLang, setDeleteLang] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null)
  const [loadingSaved, setLoadingSaved] = useState('')
  const drawers = useEditorStore((state) => (tid ? state.editors?.[tid]?.drawers : undefined))
  const tonings = useEditorStore((state) => (tid ? state.editors?.[tid]?.tonings : undefined))
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const faceKey = faceDrawKey(layers ?? [], drawers, tonings)
  const layersRef = useRef(layers)
  layersRef.current = layers
  const hasLayers = !!layers
  const faceIdentity = JSON.stringify([tid, faceKey, hasLayers, isNonSquareTemplate])
  const faceGeneration = useRef({ identity: faceIdentity, value: 0 })
  if (faceGeneration.current.identity !== faceIdentity) {
    faceGeneration.current = { identity: faceIdentity, value: faceGeneration.current.value + 1 }
  }
  const faceTicket = useRef(0)
  const loadedFor = useRef('')
  const currentFace =
    !isNonSquareTemplate && faceResult?.tid === tid && faceResult.key === faceKey &&
    faceResult.generation === faceGeneration.current.value ? faceResult : null
  const starterImage = useMemo(() => currentFace?.face.toDataURL('image/png') ?? '', [currentFace])
  const currentFaceRef = useRef(currentFace)
  currentFaceRef.current = currentFace

  useEffect(() => {
    setDeleteLang(null)
    setStarterOpen(false)
    if (!tid) return
    if (cacheTid !== tid) {
      cacheTid = tid
      cacheSession = emptySession()
      loadedFor.current = ''
    }
    setSession(cacheSession)
  }, [tid])

  const update = (next: Session) => {
    cacheTid = tid ?? ''
    cacheSession = next
    setSession(next)
  }

  useEffect(() => {
    // 五官合成按方图画布，9:16 / 3:4 先不生成
    const ticket = ++faceTicket.current
    const generation = faceGeneration.current.value
    const currentLayers = layersRef.current
    if (!tid || !currentLayers || isNonSquareTemplate) return () => { faceTicket.current++ }
    const timer = window.setTimeout(() => {
      renderEditorFace(tid, currentLayers)
        .then(({ face, anchors }) => {
          if (ticket !== faceTicket.current || generation !== faceGeneration.current.value || cacheTid !== tid) return
          const next: Session = {
            ...cacheSession,
            editorFace: face,
            faceThumbUrl: face.toDataURL('image/png'),
            faceError: '',
            langs: cacheSession.langs.map((pack) => repaintLang(pack, face)),
          }
          cacheSession = next
          setSession(next)
          setFaceResult({ tid, key: faceKey, generation, face, anchors })
          setStarterAnchors({ ...anchors })
        })
        .catch((error: unknown) => {
          if (ticket !== faceTicket.current || generation !== faceGeneration.current.value || cacheTid !== tid) return
          setFaceResult(null)
          const next: Session = {
            ...cacheSession,
            faceError: error instanceof Error ? error.message : '五官生成失败',
          }
          cacheSession = next
          setSession(next)
        })
    }, 300)
    return () => { window.clearTimeout(timer); faceTicket.current++ }
  }, [tid, hasLayers, faceKey, isNonSquareTemplate])

  const downloadStarter = async () => {
    if (!tid || !currentFace || !starterAnchors.L || !starterAnchors.R || !starterAnchors.M) return
    const ticket = faceTicket.current
    setBusy('starter')
    try {
      const { kit } = buildKitFromFace(currentFace.face, starterAnchors as Record<DotKey, Dot>)
      const blob = await buildStarterZip(tid, await canvasToPng(kit))
      if (ticket !== faceTicket.current || currentFaceRef.current !== currentFace) return
      saveBlob(blob, `${tid}_设计起始包.zip`)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: '起始包导出失败',
        description: error instanceof Error ? error.message : '未知错误',
      })
    } finally {
      setBusy(null)
    }
  }

  const importFolder = async (
    list: FileList | File[] | null,
    options?: { mode?: 'import' | 'load'; order?: Record<string, string[] | undefined> },
  ) => {
    const files = [...(list ?? [])].filter((file) => !isJunk(file.webkitRelativePath || file.name))
    if (!files.length) return
    const mode = options?.mode ?? 'import'
    setBusy(mode)
    try {
      const root = (files[0].webkitRelativePath || files[0].name).split('/')[0]
      const rootFiles: File[] = []
      const groups = new Map<string, { name: string; file: File }[]>()

      for (const file of files) {
        const parts = (file.webkitRelativePath || file.name).split('/')
        const rel = parts.slice(1)
        if (rel.length === 1) {
          rootFiles.push(file)
          continue
        }
        if (rel.length === 2) {
          const folder = rel[0]
          const rows = groups.get(folder) ?? []
          rows.push({ name: rel[1], file })
          groups.set(folder, rows)
        }
      }

      const kitFile = rootFiles.find((file) => /_face_kit\.png$/i.test(file.name))
      let kit = session.kit
      if (kitFile) {
        const image = await fileToImage(kitFile)
        const size = rasterSize(image)
        if (size.w !== STICKER_SIZE || size.h !== STICKER_SIZE) {
          throw new Error(`标准脸 ${kitFile.name} 不是 512×512`)
        }
        const pixels = readPixels(image)
        const dots = findDots(pixels.data, pixels.w)
        if (!dots.L || !dots.R || !dots.M) {
          throw new Error('目录里的标准脸找不到三个色点')
        }
        kit = {
          dots: { L: dots.L, R: dots.R, M: dots.M },
          image,
          png: kitFile,
          thumbUrl: image.src,
          label: kitFile.name,
        }
      }

      const langEntries: { lang: string; rows: { name: string; file: File }[] }[] = []
      if (groups.size) {
        for (const [folder, rows] of groups) {
          const lang = langOfFolder(folder)
          if (!lang) continue
          langEntries.push({ lang, rows })
        }
      } else {
        const lang = langOfFolder(root) ?? 'en'
        langEntries.push({
          lang,
          rows: rootFiles
            .filter((file) => !/_face_kit\.png$/i.test(file.name))
            .map((file) => ({ name: file.name, file })),
        })
      }

      const taken = new Set<string>()
      const langs: LangPack[] = []
      const langRank = (lang: string) => {
        const index = ['en', 'pt', 'es'].indexOf(lang)
        return index === -1 ? 99 : index
      }
      for (const entry of langEntries.sort(
        (a, b) => langRank(a.lang) - langRank(b.lang) || a.lang.localeCompare(b.lang),
      )) {
        const tpls = new Map<string, File>()
        const faces = new Map<string, File>()
        const notes: string[] = []
        for (const row of entry.rows) {
          const match = /^(.+?)(_face)?\.png$/i.exec(row.name)
          if (!match || /_face_kit$/i.test(match[1])) {
            notes.push(`忽略 ${row.name}`)
            continue
          }
          ;(match[2] ? faces : tpls).set(match[1], row.file)
        }
        const names = [...new Set([...tpls.keys(), ...faces.keys()])].sort(natural)
        const items: StickerItem[] = []
        for (const name of names) {
          const tpl = tpls.get(name)
          const faceFile = faces.get(name)
          if (!tpl || !faceFile) {
            notes.push(tpl ? `${name}.png 缺定位图` : `${name}_face.png 缺贴纸`)
            continue
          }
          const id = ID_RE.test(name) && !taken.has(name) ? name : newStickerId(taken)
          taken.add(id)
          const [sticker, locator] = await Promise.all([fileToImage(tpl), fileToImage(faceFile)])
          items.push({
            id,
            sticker,
            locator,
            stickerFile: tpl,
            locatorFile: faceFile,
            previewUrl: '',
            face: null,
            error: null,
            warn: null,
          })
        }
        const pack = refitLang({ lang: entry.lang, items, notes }, kit, null)
        const savedOrder = options?.order?.[entry.lang]
        if (savedOrder?.length) {
          const rank = new Map(savedOrder.map((id, index) => [id, index]))
          pack.items.sort(
            (a, b) => (rank.get(a.id) ?? 1e9) - (rank.get(b.id) ?? 1e9) || natural(a.id, b.id),
          )
        }
        langs.push(pack)
      }

      if (!langs.some((pack) => pack.items.length)) {
        throw new Error('没有找到成对的 png 和 _face.png')
      }
      const latest = cacheTid === tid ? cacheSession : session
      const faceNow = latest.editorFace
      update({
        ...latest,
        kit,
        langs: faceNow ? langs.map((pack) => repaintLang(pack, faceNow)) : langs,
        note: '',
      })
    } catch (error) {
      if (mode === 'load') loadedFor.current = ''
      toast({
        variant: 'destructive',
        title: mode === 'load' ? '加载已保存贴纸失败' : '导入失败',
        description: error instanceof Error ? error.message : '未知错误',
      })
    } finally {
      setBusy(null)
    }
  }

  const importFolderRef = useRef(importFolder)
  importFolderRef.current = importFolder

  useEffect(() => {
    if (!tid || !savedPack?.hasSrc) return
    if (cacheTid === tid && cacheSession.langs.length) return
    if (loadedFor.current === tid) return
    loadedFor.current = tid
    const order = Object.fromEntries(
      Object.entries(savedPack.langs ?? {}).map(([lang, row]) => [lang, row.ids]),
    )
    const sha = savedPack.srcSha256
    let cancelled = false
    setBusy('load')
    setLoadingSaved('正在加载已保存的贴纸')
    ;(async () => {
      try {
        let blob = sha ? await readCachedSrc(tid, sha) : null
        if (cancelled) return
        if (!blob) {
          blob = await getBlob(`/templates/${tid}/stickers/src`)
          if (cancelled) return
          if (sha) void writeCachedSrc(tid, sha, blob)
        }
        setLoadingSaved('正在打开贴纸图片')
        const zip = await JSZip.loadAsync(blob)
        const files: File[] = []
        for (const [path, entry] of Object.entries(zip.files)) {
          if (entry.dir || isJunk(path)) continue
          const parts = path.split('/').filter(Boolean)
          const name = parts[parts.length - 1]
          if (!name) continue
          const file = new File([await entry.async('arraybuffer')], name, { type: 'image/png' })
          Object.defineProperty(file, 'webkitRelativePath', {
            value: `saved/${parts.join('/')}`,
          })
          files.push(file)
        }
        if (cancelled) return
        await importFolderRef.current(files, { mode: 'load', order })
      } catch (error) {
        if (cancelled) return
        loadedFor.current = ''
        toast({
          variant: 'destructive',
          title: '加载已保存贴纸失败',
          description: error instanceof Error ? error.message : '未知错误',
        })
        setBusy(null)
      } finally {
        if (!cancelled) setLoadingSaved('')
      }
    })()
    return () => {
      cancelled = true
      if (!cacheSession.langs.length) loadedFor.current = ''
    }
  }, [tid, savedPack, toast])

  const readyGroups = session.langs
    .map((pack) => ({
      lang: pack.lang,
      items: pack.items
        .filter((item) => item.face && !item.error)
        .map((item) => ({ id: item.id, sticker: item.sticker, face: item.face as Face })),
    }))
    .filter((group) => group.items.length)

  const downloadLang = async (pack: LangPack) => {
    const items = pack.items
      .filter((item) => item.face && !item.error)
      .map((item) => ({ id: item.id, sticker: item.sticker, face: item.face as Face }))
    if (!items.length) return
    setBusy('zip')
    try {
      const blob = await buildLangZip(items)
      saveBlob(blob, `${tid ?? 'stickers'}_${pack.lang}.zip`)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: '打包失败',
        description: error instanceof Error ? error.message : '未知错误',
      })
    } finally {
      setBusy(null)
    }
  }

  const savePreview = useMemo(
    () =>
      describeSave(
        readyGroups.map((group) => ({
          lang: group.lang,
          ids: group.items.map((item) => item.id),
        })),
        savedPack,
        !!savedError,
        savedLoading,
      ),
    [readyGroups, savedPack, savedError, savedLoading],
  )

  const savePacks = async () => {
    if (!tid || !readyGroups.length) return
    setConfirmOpen(false)
    setBusy('save')
    const total = readyGroups.reduce((sum, group) => sum + group.items.length, 0)
    let packed = 0
    setSaveStatus({ phase: 'pack', label: `正在打包 0/${total}`, percent: 0 })
    try {
      const artistGroups = session.langs
        .map((pack) => ({
          lang: pack.lang,
          items: pack.items
            .filter((item) => item.stickerFile && item.locatorFile)
            .map((item) => ({
              id: item.id,
              sticker: item.stickerFile,
              locator: item.locatorFile,
            })),
        }))
        .filter((group) => group.items.length)
      const src = await buildArtistZip(tid, session.kit?.png ?? null, artistGroups)
      const form = new FormData()
      form.append('src', src, 'src.zip')
      const counts: Record<string, number> = {}
      const ids: Record<string, string[]> = {}
      for (const group of readyGroups) {
        form.append(
          group.lang,
          await buildLangZip(group.items, () => {
            packed += 1
            setSaveStatus({
              phase: 'pack',
              label: `正在打包 ${packed}/${total}`,
              percent: total ? Math.round((packed / total) * 100) : 0,
            })
          }),
          `${group.lang}.zip`,
        )
        counts[group.lang] = group.items.length
        ids[group.lang] = group.items.map((item) => item.id)
      }
      form.append('counts', JSON.stringify(counts))
      form.append('ids', JSON.stringify(ids))
      setSaveStatus({ phase: 'upload', label: '正在上传', percent: 0 })
      const saved = (await postFormWithProgress(`/templates/${tid}/stickers`, form, {
        timeout: 300000,
        onProgress: (percent: number) =>
          setSaveStatus({ phase: 'upload', label: '正在上传', percent }),
        onUploaded: () =>
          setSaveStatus({
            phase: 'write',
            label: '正在写入存储并清理缓存',
            percent: 100,
          }),
      })) as {
        cacheCleared?: boolean
        cacheError?: string
        src?: { sha256?: string }
      }
      if (saved?.src?.sha256) void writeCachedSrc(tid, saved.src.sha256, src)
      await refreshSaved(
        (current) => ({
          hasSrc: true,
          srcSha256: saved?.src?.sha256 ?? current?.srcSha256,
          langs: {
            ...(current?.langs ?? {}),
            ...Object.fromEntries(
              Object.entries(ids).map(([lang, order]) => [
                lang,
                { count: order.length, ids: order },
              ]),
            ),
          },
        }),
        { revalidate: false },
      )
      if (saved?.cacheCleared === false) {
        toast({
          variant: 'destructive',
          title: '已保存，客户端缓存没清掉',
          description: saved.cacheError || '请再点一次保存',
        })
      } else {
        toast({ title: '已保存' })
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: '保存失败',
        description: error instanceof Error ? error.message : '未知错误',
      })
    } finally {
      setBusy(null)
      setSaveStatus(null)
    }
  }

  const removeLang = async () => {
    if (!tid || !deleteLang) return
    setBusy('delete')
    try {
      const result = await delUrlFetcher(`/templates/${tid}/stickers/${deleteLang}`, { arg: null })
      if (cacheTid !== tid) return
      update({
        ...cacheSession,
        langs: cacheSession.langs.filter((pack) => pack.lang !== deleteLang),
      })
      loadedFor.current = tid
      await refreshSaved(result.index, { revalidate: false })
      if (cacheTid !== tid) return
      if (result.cacheCleared === false) {
        toast({
          variant: 'destructive',
          title: '已删除，但客户端缓存未清除',
          description: '请再次点击确认删除以重试清理缓存。',
        })
        return
      }
      setDeleteLang(null)
      toast({ title: '已删除该语言' })
    } catch (error) {
      if (cacheTid !== tid) return
      toast({
        variant: 'destructive',
        title: '删除失败',
        description: error instanceof Error ? error.message : '未知错误',
      })
    } finally {
      setBusy(null)
    }
  }

  const downloadAll = async () => {
    if (!readyGroups.length) return
    setBusy('zip')
    try {
      const blob = await buildAllZip(readyGroups)
      saveBlob(blob, `${tid ?? 'stickers'}_stickers.zip`)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: '打包失败',
        description: error instanceof Error ? error.message : '未知错误',
      })
    } finally {
      setBusy(null)
    }
  }

  const totalReady = readyGroups.reduce((sum, group) => sum + group.items.length, 0)

  const reorderLang = (lang: string, event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const pack = session.langs.find((item) => item.lang === lang)
    if (!pack) return
    const oldIndex = pack.items.findIndex((item) => item.id === active.id)
    const newIndex = pack.items.findIndex((item) => item.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    update({
      ...session,
      langs: session.langs.map((item) =>
        item.lang === lang ? { ...item, items: arrayMove(item.items, oldIndex, newIndex) } : item,
      ),
    })
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <StarterKitPreview
        open={starterOpen}
        onOpenChange={setStarterOpen}
        image={starterImage}
        anchors={starterAnchors}
        automatic={currentFace?.anchors ?? {}}
        onChange={setStarterAnchors}
        onDownload={downloadStarter}
        busy={busy === 'starter'}
        stale={!currentFace}
      />
      <section className="shrink-0 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium">当前五官</span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null || !currentFace}
            onClick={() => setStarterOpen(true)}
          >
            {busy === 'starter' ? '打包中…' : '下载给画师'}
          </Button>
        </div>
        {session.faceThumbUrl ? (
          <div className="flex items-center gap-3">
            <MyImage
              alt="当前五官"
              src={session.faceThumbUrl}
              lazy={false}
              className="h-24 w-24 rounded border bg-[repeating-conic-gradient(#eee_0_25%,#fff_0_50%)] bg-[length:12px_12px] object-contain"
            />
            <span className="text-xs text-muted-foreground">
              跟随当前选中的五官，换组件后会更新
            </span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {isNonSquareTemplate
              ? '9:16 / 3:4 模板这一版先不生成五官。'
              : session.faceError || '正在根据当前五官生成…'}
          </p>
        )}
      </section>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardHeader className="shrink-0 flex-row flex-wrap items-center gap-2 space-y-0 p-4">
          <Button
            size="sm"
            disabled={busy !== null}
            onClick={() => inputRef.current?.click()}
          >
            {busy === 'import' ? '导入中…' : busy === 'load' ? '加载中…' : '导入画师文件夹'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null || totalReady === 0}
            onClick={downloadAll}
          >
            {busy === 'zip' ? '打包中…' : `下载全部（${totalReady}）`}
          </Button>
          {loadingSaved ? (
            <Loader className="h-4 w-4">{loadingSaved}</Loader>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            multiple
            // @ts-expect-error 非标准属性，用来选整个文件夹
            webkitdirectory=""
            onChange={(event) => {
              importFolder(event.target.files)
              event.target.value = ''
            }}
          />
        </CardHeader>
        <CardContent className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {session.langs.map((pack) => {
            const ok = pack.items.filter((item) => item.face && !item.error).length
            return (
              <section key={pack.lang} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {langName(pack.lang)}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {ok}/{pack.items.length}
                    </span>
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy !== null}
                      onClick={() => setDeleteLang(pack.lang)}
                    >
                      删除该语言
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy !== null || ok === 0}
                      onClick={() => downloadLang(pack)}
                    >
                      下载该语言
                    </Button>
                  </div>
                </div>
                {pack.notes.length ? (
                  <p className="text-xs text-muted-foreground">{pack.notes.join('；')}</p>
                ) : null}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => reorderLang(pack.lang, event)}
                >
                  <SortableContext
                    items={pack.items.map((item) => item.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-[repeat(auto-fill,200px)] gap-3">
                      {pack.items.map((item, index) => (
                        <SortableStickerCard
                          key={item.id}
                          item={item}
                          index={index}
                          disabled={busy !== null}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </section>
            )
          })}
        </CardContent>
        <CardFooter className="shrink-0 flex-col items-stretch gap-2 border-t p-4">
          {saveStatus ? (
            <div className="w-full space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{saveStatus.label}</span>
                {saveStatus.phase === 'upload' ? <span>{saveStatus.percent}%</span> : null}
              </div>
              <div className="h-1.5 overflow-hidden rounded bg-muted">
                <div
                  className={`h-full bg-primary ${saveStatus.phase === 'upload' ? '' : 'animate-pulse'}`}
                  style={{
                    width: saveStatus.phase === 'upload' ? `${saveStatus.percent}%` : '100%',
                  }}
                />
              </div>
            </div>
          ) : null}
          <div className="flex w-full justify-end">
            <Button
              size="sm"
              disabled={busy !== null || totalReady === 0}
              onClick={() => setConfirmOpen(true)}
            >
              {busy === 'save' ? '保存中…' : '保存'}
            </Button>
          </div>
        </CardFooter>
      </Card>
      <Dialog
        open={deleteLang !== null}
        onOpenChange={(open) => {
          if (!open && busy !== 'delete') setDeleteLang(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除{deleteLang ? langName(deleteLang) : ''}贴纸？</DialogTitle>
            <DialogDescription>
              该语言整组贴纸将从当前列表移除；已保存的客户端包会立即删除，无需再点保存。其他语言不受影响。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy !== null} onClick={() => setDeleteLang(null)}>
              取消
            </Button>
            <Button variant="destructive" disabled={busy !== null} onClick={removeLang}>
              {busy === 'delete' ? '删除中…' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认保存</DialogTitle>
            <DialogDescription>保存后客户端会读到这次的贴纸包。</DialogDescription>
          </DialogHeader>
          <ul className="max-h-64 space-y-2 overflow-y-auto text-sm">
            {savePreview.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              取消
            </Button>
            <Button disabled={savedLoading || busy !== null} onClick={savePacks}>
              确认保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default StickerPanel
